import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Shield, Menu, X, LogOut, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, isAdmin } = useAuth();
  const { language, changeLanguage } = useLanguage();

  const getNavLinks = () => {
    const links = {
      ru: [
        { path: '/', label: 'Главная' },
        { path: '/analyzer', label: 'Анализатор' },
        { path: '/io-types', label: 'Типы ИО' },
        { path: '/thesaurus', label: 'Тезаурус' }
      ],
      kk: [
        { path: '/', label: 'Басты' },
        { path: '/analyzer', label: 'Талдау' },
        { path: '/io-types', label: 'ИО түрлері' },
        { path: '/thesaurus', label: 'Тезаурус' }
      ],
      en: [
        { path: '/', label: 'Home' },
        { path: '/analyzer', label: 'Analyzer' },
        { path: '/io-types', label: 'IO Types' },
        { path: '/thesaurus', label: 'Thesaurus' }
      ]
    };

    const baseLinks = links[language] || links.ru;

    if (isAuthenticated) {
      const statsLabel = { ru: 'Статистика', kk: 'Статистика', en: 'Statistics' }[language] || 'Статистика';
      const adminLabel = { ru: 'Админ', kk: 'Админ', en: 'Admin' }[language] || 'Админ';
      baseLinks.push({ path: '/statistics', label: statsLabel });
      if (isAdmin) {
        baseLinks.push({ path: '/admin', label: adminLabel });
      }
    }

    return baseLinks;
  };

  const navLinks = getNavLinks();
  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 font-bold text-xl text-gray-900" data-testid="navbar-logo">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="hidden sm:inline">Система анализа ИО</span>
            <span className="sm:hidden">ИО</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                data-testid={`nav-link-${link.path}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* User Menu / Auth Links */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors text-sm">
                  {language.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => changeLanguage('ru')} className="cursor-pointer">
                  {language === 'ru' && '✓ '} Русский
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('kk')} className="cursor-pointer">
                  {language === 'kk' && '✓ '} Қазақша
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('en')} className="cursor-pointer">
                  {language === 'en' && '✓ '} English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                    <User className="w-4 h-4" />
                    <span className="font-medium truncate max-w-[120px]">{user?.username}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-4 py-2 text-sm">
                    <p className="font-medium text-gray-900">{user?.username}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    <p className="text-xs text-gray-500 capitalize">
                      {language === 'ru' && 'Роль: '}
                      {language === 'kk' && 'Рөлі: '}
                      {language === 'en' && 'Role: '}
                      {user?.role}
                    </p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    {language === 'ru' && 'Выход'}
                    {language === 'kk' && 'Шығу'}
                    {language === 'en' && 'Sign Out'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50 font-medium transition-colors"
                >
                  {language === 'ru' && 'Вход'}
                  {language === 'kk' && 'Кіру'}
                  {language === 'en' && 'Sign In'}
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium transition-colors"
                >
                  {language === 'ru' && 'Регистрация'}
                  {language === 'kk' && 'Тіркеу'}
                  {language === 'en' && 'Sign Up'}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-gray-200" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                data-testid={`mobile-nav-link-${link.path}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Language Selector Mobile */}
            <div className="px-4 py-3 border-t border-gray-200 mt-2">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-2">
                {language === 'ru' && 'Язык'}
                {language === 'kk' && 'Тіл'}
                {language === 'en' && 'Language'}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => changeLanguage('ru')}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                    language === 'ru' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  РУ
                </button>
                <button
                  onClick={() => changeLanguage('kk')}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                    language === 'kk' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  КК
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                    language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  EN
                </button>
              </div>
            </div>

            {isAuthenticated && (
              <>
                <div className="py-2 border-t border-gray-200 mt-2 px-4">
                  <p className="text-sm font-medium text-gray-900">{user?.username}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <p className="text-xs text-gray-500 capitalize">
                    {language === 'ru' && 'Роль: '}
                    {language === 'kk' && 'Рөлі: '}
                    {language === 'en' && 'Role: '}
                    {user?.role}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg flex items-center gap-2 mt-2 font-medium transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  {language === 'ru' && 'Выход'}
                  {language === 'kk' && 'Шығу'}
                  {language === 'en' && 'Sign Out'}
                </button>
              </>
            )}
            {!isAuthenticated && (
              <div className="flex gap-2 px-4 mt-4">
                <Link
                  to="/login"
                  className="flex-1 px-4 py-2 rounded-lg text-center text-gray-600 hover:bg-gray-50 font-medium transition-colors border border-gray-300"
                  onClick={() => setIsOpen(false)}
                >
                  {language === 'ru' && 'Вход'}
                  {language === 'kk' && 'Кіру'}
                  {language === 'en' && 'Sign In'}
                </Link>
                <Link
                  to="/register"
                  className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-center font-medium transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {language === 'ru' && 'Регистрация'}
                  {language === 'kk' && 'Тіркеу'}
                  {language === 'en' && 'Sign Up'}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;